import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useApprovedReports, useFinancialDashboard } from '../api/hooks';
import type { ApprovedReportView } from '../api/types';
import { Button, DataTable, EmptyState } from '../components/DesignSystem';
import type { DataTableColumn } from '../components/DesignSystem';
import { PageHeader } from '../components/PageHeader';
import { formatCurrency, formatDate, formatNumber } from '../lib/format';

interface PartyPayout {
  partyName: string;
  total: number;
}

const REPORT_COLUMNS: DataTableColumn<ApprovedReportView>[] = [
  { key: 'reportNumber', header: 'Report #' },
  { key: 'showName', header: 'Show' },
  { key: 'seasonCode', header: 'Season' },
  {
    key: 'net',
    header: 'Net Receipts',
    align: 'right',
    render: (row) => formatCurrency(row.snapshot.netTotal),
  },
  {
    key: 'due',
    header: 'Due to Pay',
    align: 'right',
    render: (row) => formatCurrency(row.snapshot.distributedTotal),
  },
  { key: 'approvedAt', header: 'Approved', render: (row) => formatDate(row.approvedAt) },
];

const PAYOUT_COLUMNS: DataTableColumn<PartyPayout>[] = [
  { key: 'partyName', header: 'Party' },
  {
    key: 'total',
    header: 'Due to Pay',
    align: 'right',
    render: (row) => formatCurrency(row.total),
  },
];

function rollupPayouts(reports: ApprovedReportView[]): PartyPayout[] {
  const totals = reports.reduce<Map<string, number>>((accumulator, report) => {
    return report.snapshot.perParty.reduce((inner, party) => {
      const next = new Map(inner);
      next.set(party.partyName, (next.get(party.partyName) ?? 0) + party.total);
      return next;
    }, accumulator);
  }, new Map());

  return Array.from(totals.entries())
    .map(([partyName, total]) => ({ partyName, total }))
    .sort((a, b) => b.total - a.total);
}

/** `/` — revenue, net receipts, and payout stats across approved reports. */
export function DashboardPage() {
  const navigate = useNavigate();
  const dashboard = useFinancialDashboard();
  const reports = useApprovedReports({ limit: 50 });

  const reportRows = useMemo(() => reports.data?.data ?? [], [reports.data]);
  const payouts = useMemo(() => rollupPayouts(reportRows), [reportRows]);

  const stats = dashboard.data;
  const hasData =
    stats !== undefined &&
    (stats.totalRevenue !== 0 ||
      stats.netReceipts !== 0 ||
      stats.dueToPayOut !== 0 ||
      stats.activeShows !== 0);

  const statValue = (value: number | undefined, formatter: (v: number) => string) => {
    if (dashboard.isLoading || value === undefined) {
      return '…';
    }

    return formatter(value);
  };

  return (
    <>
      <PageHeader
        caption="Revenue, net receipts, and counterparty payouts across every approved reporting window."
        title="Financial Dashboard"
      />

      {dashboard.isError && (
        <p className="rfp-inline-error" role="alert">
          Could not load financial metrics — please retry.
        </p>
      )}

      <div className="rfp-stat-grid wf-dashboard-stats">
        <article className="rfp-stat-card">
          <div className="rfp-stat-card__top">
            <span className="rfp-stat-card__label">
              <span aria-hidden="true" className="rfp-stat-card__rule" />
              Total Revenue
            </span>
          </div>
          <p className="rfp-stat-card__value">{statValue(stats?.totalRevenue, formatCurrency)}</p>
          <p className="rfp-stat-card__caption">
            {hasData ? 'Gross across approved reports' : 'Upload a contract to begin'}
          </p>
        </article>
        <article className="rfp-stat-card">
          <div className="rfp-stat-card__top">
            <span className="rfp-stat-card__label">
              <span aria-hidden="true" className="rfp-stat-card__rule" />
              Net Receipts
            </span>
          </div>
          <p className="rfp-stat-card__value">{statValue(stats?.netReceipts, formatCurrency)}</p>
          <p className="rfp-stat-card__caption">After commissions &amp; expenses</p>
        </article>
        <article className="rfp-stat-card">
          <div className="rfp-stat-card__top">
            <span className="rfp-stat-card__label">
              <span aria-hidden="true" className="rfp-stat-card__rule" />
              Due to Pay Out
            </span>
          </div>
          <p className="rfp-stat-card__value">{statValue(stats?.dueToPayOut, formatCurrency)}</p>
          <p className="rfp-stat-card__caption">Allocated across all parties</p>
        </article>
        <article className="rfp-stat-card">
          <div className="rfp-stat-card__top">
            <span className="rfp-stat-card__label">
              <span aria-hidden="true" className="rfp-stat-card__rule" />
              Active Shows
            </span>
          </div>
          <p className="rfp-stat-card__value">{statValue(stats?.activeShows, formatNumber)}</p>
          <p className="rfp-stat-card__caption">With approved reports</p>
        </article>
      </div>

      {reports.isLoading ? (
        <p className="rfp-loading">Loading reports…</p>
      ) : reportRows.length === 0 ? (
        <section className="rfp-card wf-dashboard-empty">
          <EmptyState
            action={
              <Button onClick={() => navigate('/upload')} type="button">
                Upload Contract
              </Button>
            }
            caption="Upload a contract, import sales data, and approve a report to see revenue and payout figures here."
            icon={<span aria-hidden="true">£</span>}
            title="No Financial Data Yet"
          />
        </section>
      ) : (
        <>
          <section className="rfp-card">
            <div className="rfp-card__head">
              <div>
                <h2 className="rfp-card__title">Approved Reports ({reportRows.length})</h2>
                <p className="rfp-card__caption">
                  Every approved reporting window, with net receipts and amounts due to pay.
                </p>
              </div>
            </div>
            <DataTable
              columns={REPORT_COLUMNS}
              emptyMessage="No approved reports yet"
              rowKey={(row) => row.id}
              rows={reportRows}
            />
          </section>

          <section className="rfp-card">
            <div className="rfp-card__head">
              <div>
                <h2 className="rfp-card__title">Payouts by Party</h2>
                <p className="rfp-card__caption">
                  Rolled up across all approved reports, sorted by amount due.
                </p>
              </div>
            </div>
            <DataTable
              columns={PAYOUT_COLUMNS}
              emptyMessage="No party payouts yet"
              rowKey={(row) => row.partyName}
              rows={payouts}
            />
          </section>
        </>
      )}
    </>
  );
}
